import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { translateZoneName, translateZoneNameToEnglish, clearTranslationCache } from '@medical/shared/utils/translateZoneName';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import AudioPlayer from '../components/AudioPlayer';
import { TranslatedText } from '@medical/shared/components/TranslatedText';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend);

const PainLogsContainer = styled.div`
  max-width: ${props => props.compact ? '100%' : '1200px'};
  margin: ${props => props.compact ? '0' : '0 auto'};
  padding: ${props => props.compact ? '0' : '2rem 1rem'};

  @media (max-width: 768px) {
    padding: ${props => props.compact ? '0' : '1rem 0.75rem'};
  }

  @media (max-width: 480px) {
    padding: ${props => props.compact ? '0' : '0.75rem 0.5rem'};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    flex-direction: column;
    align-items: stretch;
  }

  @media (max-width: 480px) {
    margin-bottom: 1rem;
    gap: 0.75rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }

  @media (max-width: 480px) {
    font-size: 1.5rem;
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #2563eb;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LogsContainer = styled.div`
  background: white;
  border-radius: 1rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const LogsHeader = styled.div`
  background: #f8fafc;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1.5fr auto;
  gap: 1rem;
  align-items: center;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LogItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f3f4f6;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1.5fr auto;
  gap: 1rem;
  align-items: center;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }
`;

const LogZone = styled.div`
  font-weight: 500;
  color: #374151;
`;

const LogPainLevel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PainIndicator = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    // Handle hex colors directly
    if (props.color && props.color.startsWith('#')) {
      return props.color;
    }
    // Handle color names
    switch (props.color) {
      case 'red': return '#ef4444';
      case 'yellow': return '#f59e0b';
      case 'green': return '#10b981';
      case 'blue': return '#60a5fa';
      default: return '#e5e7eb';
    }
  }};
`;

const PainLevelText = styled.span`
  font-size: 0.875rem;
  color: ${props => {
    // If painColor is provided and valid, use it (prioritize stored color)
    if (props.painColor && props.painColor.startsWith('#')) {
      return props.painColor;
    }
    // Otherwise, map from painLevel
    switch (props.level) {
      case 'worst-pain': return '#ef4444';
      case 'severe': return '#f97316';
      case 'moderate': return '#f59e0b';
      case 'mild': return '#60a5fa';
      case 'no-pain': return '#10b981';
      default: return '#6b7280';
    }
  }};
  font-weight: 500;
`;

const LogIntensity = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const LogTime = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const LogNotes = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const NotesText = styled.div`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LogActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
  }
  
  &.delete {
    color: #ef4444;
    border-color: #fecaca;
    
    &:hover {
      background: #fef2f2;
    }
  }
`;

const MobileLogItem = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
`;

const MobileLogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MobileLogTime = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
`;

const MobileLogDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const MobileLogNotes = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
`;

const MobileLogActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.5rem;
`;

const EmptyTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
`;

const EmptyText = styled.p`
  margin: 0;
`;

const PostMedicationSection = styled.section`
  margin-top: 2rem;
  padding: 1.25rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
`;

const PostMedicationTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
`;

const ChartWrapper = styled.div`
  position: relative;
  height: 280px;
  margin-top: 0.75rem;
`;

const PainLogs = ({ patientId = null, compact = false }) => {
  const { t, i18n } = useTranslation(); // Use translation hook for reactivity to language changes
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render on language change
  const [postMedicationLogs, setPostMedicationLogs] = useState([]);
  
  // Listen to language changes and refresh
  useEffect(() => {
    const handleLanguageChange = () => {
      clearTranslationCache(); // Clear cache when language changes
      setRefreshKey(prev => prev + 1); // Force component re-render
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);
  
  // Helper function to get translations - respects current language preference
  const getT = (key, options = {}) => {
    return t(key, options);
  };
  
  // Ensure logs is always an array
  const safeLogs = Array.isArray(logs) ? logs : [];
  const [filters, setFilters] = useState({
    painLevel: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const fetchLogs = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        ...filters
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      let response;
      if (patientId) {
        // Fetch for specific patient (doctor view)
        const token = localStorage.getItem('doctorToken');
        if (!token) {
          throw new Error('Doctor authentication required');
        }
        
        if (!patientId || patientId.trim() === '') {
          throw new Error('Patient ID is required');
        }
        
        console.log('Fetching pain logs for patient:', patientId);
        response = await axios.get(`/api/pain-logs/patient/${patientId}`, {
          params: {
            limit: pagination.itemsPerPage * pagination.currentPage,
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.painLevel && { painLevel: filters.painLevel })
          },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('Pain logs API response:', response.data);
        
        // Check if response has the expected structure
        if (!response.data) {
          throw new Error('Invalid response from server');
        }
        
        if (!response.data.success) {
          throw new Error(response.data?.message || 'Failed to fetch patient logs');
        }
        
        const allLogs = response.data.data?.painLogs || [];
        console.log('Extracted pain logs:', allLogs.length, 'logs');
        // Simple pagination for patient endpoint
        const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
        const endIndex = startIndex + pagination.itemsPerPage;
        setLogs(allLogs.slice(startIndex, endIndex));
        setPagination(prev => ({
          ...prev,
          totalItems: allLogs.length,
          totalPages: Math.ceil(allLogs.length / pagination.itemsPerPage)
        }));
      } else {
        // Normal patient view
        response = await axios.get('/api/pain-logs', { params });
        const painLogs = response.data?.painLogs;
        setLogs(Array.isArray(painLogs) ? painLogs : []);
        setPagination(prev => ({
          ...prev,
          ...response.data.pagination
        }));
      }
    } catch (err) {
      console.error('Error fetching pain logs:', err);
      const errorMessage = err.response?.data?.message || err.message || getT('painLogs.error');
      setError(errorMessage);
      setLogs([]); // Ensure logs is always an array even on error
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage, patientId]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Fetch post-medication pain logs when viewing a patient (doctor view)
  useEffect(() => {
    if (!patientId) {
      setPostMedicationLogs([]);
      return;
    }
    const token = localStorage.getItem('doctorToken');
    if (!token) return;
    axios
      .get(`/api/pain-logs/patient/${patientId}`, {
        params: { postMedicationOnly: 'true', limit: 500 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data?.painLogs)) {
          setPostMedicationLogs(res.data.data.painLogs);
        } else {
          setPostMedicationLogs([]);
        }
      })
      .catch(() => setPostMedicationLogs([]));
  }, [patientId]);

  // Chart data: Pain intensity vs. minutes after dose (grouped by medication)
  const postMedicationChartData = useMemo(() => {
    if (!postMedicationLogs.length) return null;
    const byMed = {};
    const allMinutesSet = new Set();
    postMedicationLogs.forEach((log) => {
      const name = log.postMedicationMedicationName || 'Medication';
      if (!byMed[name]) byMed[name] = [];
      const takenAt = log.medicationTakenAt ? new Date(log.medicationTakenAt).getTime() : 0;
      const loggedAt = log.timestamp ? new Date(log.timestamp).getTime() : 0;
      const minutesAfter = takenAt ? Math.round((loggedAt - takenAt) / 60000) : 0;
      allMinutesSet.add(minutesAfter);
      byMed[name].push({ minutesAfter, painIntensity: log.painIntensity ?? 0 });
    });
    const sortedMinutes = [...allMinutesSet].sort((a, b) => a - b);
    const labels = sortedMinutes.map((m) => `${m} min`);
    if (labels.length === 0) return null;
    const colors = ['#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed'];
    const datasets = Object.entries(byMed).map(([name, points], i) => {
      const valueByMin = {};
      points.forEach((p) => {
        valueByMin[p.minutesAfter] = p.painIntensity;
      });
      const data = sortedMinutes.map((min) => valueByMin[min] ?? null);
      return {
        label: name,
        data,
        borderColor: colors[i % colors.length],
        backgroundColor: colors[i % colors.length] + '20',
        tension: 0.2,
        fill: false,
      };
    });
    return { labels, datasets };
  }, [postMedicationLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page
    }));
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm(getT('painLogs.deleteConfirm'))) {
      return;
    }

    try {
      await axios.delete(`/api/pain-logs/${logId}`);
      setLogs(prev => (Array.isArray(prev) ? prev : []).filter(log => log._id !== logId));
      toast.success(getT('painLogs.deleteSuccess'));
    } catch (err) {
      console.error('Error deleting pain log:', err);
      toast.error(getT('painLogs.deleteError'));
    }
  };

  const getPainLevelText = (level) => {
    switch (level) {
      case 'worst-pain': return getT('painLogs.filters.worstPain');
      case 'severe': return getT('painLogs.filters.severePain');
      case 'moderate': return getT('painLogs.filters.moderatePain');
      case 'mild': return getT('painLogs.filters.mild');
      case 'no-pain': return getT('painLogs.filters.noPain');
      default: return level;
    }
  };

  const formatDateTime = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      const lang = i18n.language || 'en';
      const localeMap = {
        'en': 'en-US',
        'kn': 'kn-IN',
        'ta': 'ta-IN',
        'ml': 'ml-IN',
        'hi': 'hi-IN',
        'mr': 'mr-IN',
        'te': 'te-IN'
      };
      const locale = localeMap[lang] || 'en-US';
      
      const dateStr = new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }).format(date);
      
      const timeStr = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
      
      return {
        date: dateStr,
        time: timeStr
      };
    } catch (e) {
      // Fallback to English format if locale formatting fails
      const date = parseISO(timestamp);
      return {
        date: format(date, 'MMM dd, yyyy'),
        time: format(date, 'h:mm a')
      };
    }
  };

  if (loading) {
    return <LoadingSpinner text={getT('painLogs.loading')} />;
  }

  if (error) {
    return (
      <PainLogsContainer compact={compact}>
        <div style={{ textAlign: 'center', padding: '2rem', background: 'white', borderRadius: '0.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <EmptyIcon>⚠️</EmptyIcon>
          <EmptyTitle>{getT('painLogs.error')}</EmptyTitle>
          <EmptyText>{error}</EmptyText>
          <Button onClick={fetchLogs} style={{ marginTop: '1rem' }}>
            {getT('dashboard.actions.retry')}
          </Button>
        </div>
      </PainLogsContainer>
    );
  }

  return (
    <PainLogsContainer compact={compact}>
      {!compact && (
        <Header>
          <Title>{getT('painLogs.title')}</Title>
        <Filters>
          <FilterGroup>
            <Label>{getT('painLogs.filters.painLevel')}</Label>
            <Select
              value={filters.painLevel}
              onChange={(e) => handleFilterChange('painLevel', e.target.value)}
            >
              <option value="">{getT('painLogs.filters.allLevels')}</option>
              <option value="no-pain">{getT('painLogs.filters.noPain')}</option>
              <option value="mild">{getT('painLogs.filters.mild')}</option>
              <option value="moderate">{getT('painLogs.filters.moderatePain')}</option>
              <option value="severe">{getT('painLogs.filters.severePain')}</option>
              <option value="worst-pain">{getT('painLogs.filters.worstPain')}</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>{getT('painLogs.filters.startDate')}</Label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>{getT('painLogs.filters.endDate')}</Label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </FilterGroup>

          <Button onClick={() => setFilters({ painLevel: '', startDate: '', endDate: '' })}>
            {getT('painLogs.filters.clear')}
          </Button>
        </Filters>
        </Header>
      )}
      
      {compact && (
        <Filters style={{ marginBottom: '1rem', padding: '1rem', background: 'white', borderRadius: '0.5rem' }}>
          <FilterGroup>
            <Label>{getT('painLogs.filters.painLevel')}</Label>
            <Select
              value={filters.painLevel}
              onChange={(e) => handleFilterChange('painLevel', e.target.value)}
            >
              <option value="">{getT('painLogs.filters.allLevels')}</option>
              <option value="no-pain">{getT('painLogs.filters.noPain')}</option>
              <option value="mild">{getT('painLogs.filters.mild')}</option>
              <option value="moderate">{getT('painLogs.filters.moderatePain')}</option>
              <option value="severe">{getT('painLogs.filters.severePain')}</option>
              <option value="worst-pain">{getT('painLogs.filters.worstPain')}</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>{getT('painLogs.filters.startDate')}</Label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>{getT('painLogs.filters.endDate')}</Label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </FilterGroup>

          <Button onClick={() => setFilters({ painLevel: '', startDate: '', endDate: '' })}>
            {getT('painLogs.filters.clear')}
          </Button>
        </Filters>
      )}

      {patientId && (
        <PostMedicationSection style={{ marginTop: 0, marginBottom: '1.5rem' }}>
          <PostMedicationTitle>
            {getT('painLogs.postMedicationGraph', { defaultValue: 'Pain vs. time after medication' })}
          </PostMedicationTitle>
          <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#64748b' }}>
            {getT('painLogs.postMedicationGraphDesc', { defaultValue: 'Pain level recorded ~30 min after taking medication. Compare to regular pain logs to see medication effect.' })}
          </p>
          {(postMedicationChartData && postMedicationChartData.datasets.length > 0) ? (
            <ChartWrapper>
              <Line
                data={postMedicationChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    x: {
                      title: { display: true, text: getT('painLogs.minutesAfterDose', { defaultValue: 'Minutes after dose' }) },
                    },
                    y: {
                      min: 0,
                      max: 10,
                      title: { display: true, text: getT('painLogs.painIntensity', { defaultValue: 'Pain (0-10)' }) },
                    },
                  },
                  plugins: {
                    legend: { position: 'top' },
                    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}/10` } },
                  },
                }}
              />
            </ChartWrapper>
          ) : (
            <>
              <ChartWrapper>
                <Line
                  data={{
                    labels: ['0 min', '30 min', '60 min', '90 min'],
                    datasets: [{
                      label: getT('painLogs.sampleData', { defaultValue: '(Sample – no data yet)' }),
                      data: [6, 4, 3, 2],
                      borderColor: '#94a3b8',
                      backgroundColor: 'rgba(148, 163, 184, 0.1)',
                      borderDash: [5, 5],
                      tension: 0.2,
                      fill: false,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      x: {
                        title: { display: true, text: getT('painLogs.minutesAfterDose', { defaultValue: 'Minutes after dose' }) },
                      },
                      y: {
                        min: 0,
                        max: 10,
                        title: { display: true, text: getT('painLogs.painIntensity', { defaultValue: 'Pain (0-10)' }) },
                      },
                    },
                    plugins: {
                      legend: { position: 'top' },
                    },
                  }}
                />
              </ChartWrapper>
              <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem', color: '#64748b' }}>
                {getT('painLogs.postMedicationEmpty', { defaultValue: 'No pain-after-medication logs yet. Patient can mark "I took it" on Medication page and record pain ~30 min later.' })}
              </p>
            </>
          )}
        </PostMedicationSection>
      )}

      {safeLogs.length > 0 ? (
        <>
          <LogsContainer>
            <LogsHeader>
              <div>{getT('painLogs.table.zone')}</div>
              <div>{getT('painLogs.table.painLevel')}</div>
              <div>{getT('painLogs.table.intensity')}</div>
              <div>{getT('painLogs.table.date')}</div>
              <div>{getT('painLogs.table.notes')}</div>
              <div>{getT('painLogs.table.actions')}</div>
            </LogsHeader>

            {safeLogs.map((log) => {
              const { date, time } = formatDateTime(log.timestamp);
              return (
                <LogItem key={`${log._id}-${i18n.language}-${refreshKey}`}>
                  <LogZone>{patientId ? translateZoneNameToEnglish(log.zoneName) : translateZoneName(log.zoneName, t)}</LogZone>
                  <LogPainLevel>
                    <PainIndicator color={log.painColor} />
                    <PainLevelText level={log.painLevel} painColor={log.painColor}>
                      {getPainLevelText(log.painLevel)}
                    </PainLevelText>
                  </LogPainLevel>
                  <LogIntensity>{log.painIntensity}/10</LogIntensity>
                  <LogTime>
                    <div>{date}</div>
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{time}</div>
                  </LogTime>
                  <LogNotes>
                    {log.notes && (
                      <NotesText title={log.notes}>
                        <TranslatedText text={log.notes} />
                      </NotesText>
                    )}
                    {log.audioNote && log.audioNote.filename && (
                      <AudioPlayer 
                        audioNote={log.audioNote} 
                        token={patientId ? localStorage.getItem('doctorToken') : undefined}
                      />
                    )}
                    {!log.notes && !log.audioNote && '-'}
                  </LogNotes>
                  {!patientId && (
                    <LogActions>
                      <ActionButton
                        className="delete"
                        onClick={() => handleDeleteLog(log._id)}
                      >
                        {getT('painLogs.actions.delete')}
                      </ActionButton>
                    </LogActions>
                  )}
                  {patientId && <div></div>}
                </LogItem>
              );
            })}
          </LogsContainer>

          {pagination.totalPages > 1 && (
            <Pagination>
              <PaginationButton
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                {getT('painLogs.actions.previous')}
              </PaginationButton>

              {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                <PaginationButton
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={page === pagination.currentPage ? 'active' : ''}
                >
                  {page}
                </PaginationButton>
              ))}

              <PaginationButton
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                {getT('painLogs.actions.next')}
              </PaginationButton>
            </Pagination>
          )}

        </>
      ) : (
        <EmptyState>
          <EmptyIcon>📝</EmptyIcon>
          <EmptyTitle>{getT('painLogs.empty')}</EmptyTitle>
          <EmptyText>
            {Object.values(filters).some(filter => filter !== '') 
              ? getT('painLogs.emptyFiltered', { defaultValue: 'Try adjusting your filters to see more results' })
              : getT('painLogs.emptyText', { defaultValue: 'Start tracking your pain by using the body diagram' })
            }
          </EmptyText>
        </EmptyState>
      )}
    </PainLogsContainer>
  );
};

export default PainLogs;
